import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { BusinessDeveloperTurnoverOverview, BusinessDeveloperTurnoverTarget } from '../../models/Financial/DivisionTurnoverOverview';
import { FinancialService } from '../financial.service';

interface TargetRow {
    month: number;
    label: string;
    targets: { [businessDeveloperUuid: string]: number };
}

@Component({
    selector: 'app-business-developer-target-modal',
    templateUrl: './business-developer-target-modal.component.html',
    styleUrls: ['../division-target-modal/division-target-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class BusinessDeveloperTargetModalComponent implements OnInit {
    @Input() year: number;
    @Input() businessDevelopers: BusinessDeveloperTurnoverOverview[] = [];
    @Input() existingTargets: BusinessDeveloperTurnoverTarget[] = [];

    saving = false;
    rows: TargetRow[] = [];

    readonly months = [
        { value: 1, label: 'Jan' },
        { value: 2, label: 'Feb' },
        { value: 3, label: 'Mar' },
        { value: 4, label: 'Apr' },
        { value: 5, label: 'May' },
        { value: 6, label: 'Jun' },
        { value: 7, label: 'Jul' },
        { value: 8, label: 'Aug' },
        { value: 9, label: 'Sep' },
        { value: 10, label: 'Oct' },
        { value: 11, label: 'Nov' },
        { value: 12, label: 'Dec' },
    ];

    constructor(public activeModal: NgbActiveModal, private financialService: FinancialService, private toastr: ToastrService) {}

    ngOnInit(): void {
        this.rows = this.months.map((month) => {
            const targets: { [businessDeveloperUuid: string]: number } = {};
            this.businessDevelopers.forEach((businessDeveloper) => {
                const existing = this.existingTargets.find(
                    (target) => target.businessDeveloperUuid === businessDeveloper.businessDeveloperUuid && target.month === month.value,
                );
                targets[businessDeveloper.businessDeveloperUuid] = existing ? existing.targetAmount : 0;
            });
            return { month: month.value, label: month.label, targets };
        });
    }

    onValueChanged(row: TargetRow, businessDeveloperUuid: string, value: string | number): void {
        const parsed = typeof value === 'string' ? parseFloat(value) : value;
        row.targets[businessDeveloperUuid] = isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
    }

    getBusinessDeveloperTotal(businessDeveloperUuid: string): number {
        return this.rows.reduce((sum, row) => sum + (row.targets[businessDeveloperUuid] || 0), 0);
    }

    getMonthTotal(row: TargetRow): number {
        return this.businessDevelopers.reduce((sum, businessDeveloper) => sum + (row.targets[businessDeveloper.businessDeveloperUuid] || 0), 0);
    }

    getGrandTotal(): number {
        return this.businessDevelopers.reduce((sum, businessDeveloper) => sum + this.getBusinessDeveloperTotal(businessDeveloper.businessDeveloperUuid), 0);
    }

    copyToAllMonths(businessDeveloperUuid: string, sourceRow: TargetRow): void {
        const value = sourceRow.targets[businessDeveloperUuid] || 0;
        this.rows.forEach((row) => {
            row.targets[businessDeveloperUuid] = value;
        });
    }

    save(): void {
        const targets: BusinessDeveloperTurnoverTarget[] = [];
        this.rows.forEach((row) => {
            this.businessDevelopers.forEach((businessDeveloper) => {
                targets.push({
                    businessDeveloperUuid: businessDeveloper.businessDeveloperUuid,
                    year: this.year,
                    month: row.month,
                    targetAmount: row.targets[businessDeveloper.businessDeveloperUuid] || 0,
                });
            });
        });

        this.saving = true;
        this.financialService.upsertBusinessDeveloperTurnoverTargets(targets).subscribe({
            next: () => {
                this.toastr.success('Business developer targets saved.');
                this.activeModal.close(true);
            },
            error: () => {
                this.toastr.error('Failed to save business developer targets.');
                this.saving = false;
            },
        });
    }

    clearAll(): void {
        this.rows.forEach((row) => {
            this.businessDevelopers.forEach((businessDeveloper) => {
                row.targets[businessDeveloper.businessDeveloperUuid] = 0;
            });
        });
    }
}
